import { useState } from 'react'
import { useCookieConsentStore } from '@/stores/cookie-consent.store'

export function CookieConsentBanner() {
  const { hasConsented, preferences, acceptAll, rejectOptional, savePreferences } =
    useCookieConsentStore()
  const [showDetails, setShowDetails] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(preferences.analytics)

  // Don't show if user has already consented
  if (hasConsented) return null

  const handleSavePreferences = () => {
    savePreferences({ analytics: analyticsEnabled })
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4">
      <div className="mx-auto max-w-4xl">
        <div className="rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
          {/* Main banner */}
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  We value your privacy
                </h2>
                <p className="text-sm text-gray-600">
                  We use cookies to improve your experience, analyse site usage, and help us
                  understand how our service is used. You can choose which cookies to allow.{' '}
                  <a href="/privacy#cookies" className="text-primary-600 hover:underline">
                    Learn more
                  </a>
                </p>
              </div>

              {/* Quick action buttons - shown when details are hidden */}
              {!showDetails && (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Customise
                  </button>
                  <button
                    onClick={rejectOptional}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reject Optional
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Accept All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Detailed preferences panel */}
          {showDetails && (
            <div className="border-t border-gray-200 bg-gray-50 p-4 sm:p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Cookie Preferences</h3>

              <div className="space-y-4">
                {/* Essential cookies - always on */}
                <div className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm">Essential Cookies</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        Always Active
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Required for the app to function. These include authentication, security,
                      and session management cookies.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="h-5 w-5 rounded border-gray-300 text-primary-600 cursor-not-allowed opacity-50"
                    />
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <label
                      htmlFor="analytics-toggle"
                      className="font-medium text-gray-900 text-sm cursor-pointer"
                    >
                      Analytics Cookies
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Help us understand how you use Brightseed so we can improve our service.
                      This includes page views, feature usage, and error tracking. No personal
                      content (like messages or updates) is ever tracked.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <button
                      id="analytics-toggle"
                      role="switch"
                      aria-checked={analyticsEnabled}
                      onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        analyticsEnabled ? 'bg-primary-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          analyticsEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action buttons for detailed view */}
              <div className="flex flex-col-reverse sm:flex-row gap-2 mt-6">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <div className="flex gap-2 sm:ml-auto">
                  <button
                    onClick={rejectOptional}
                    className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reject All Optional
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
